import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Spinner } from "@/components/common";
import CoursePlayer from "@/pages/learner/CoursePlayer";

const post = (type, payload) => {
  try { window.parent?.postMessage({ source: "tmc-learning", type, ...payload }, "*"); } catch { /* no parent */ }
};

/** Bare-chrome player for partner applications (Therapy Quest). Exchanges a one-time launch token,
 *  renders the normal course player, and relays progress to the host page via postMessage. */
export default function EmbedPlayer() {
  const [state, setState] = useState("exchanging");
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const launchToken = new URLSearchParams(window.location.hash.slice(1)).get("launch");
    window.history.replaceState(null, "", window.location.pathname);
    if (!launchToken) { setState("error"); setError("This page must be opened from Therapy Quest."); return; }
    (async () => {
      try {
        const { data } = await api.post("/partner/embed/exchange", { launchToken });
        sessionStorage.setItem("tmc_embed_token", data.token);
        setEnrollmentId(data.enrollmentId);
        setState("ready");
        post("ready", { enrollmentId: data.enrollmentId, user: data.user });
      } catch (e) {
        setState("error");
        setError(e?.response?.data?.detail || "We could not start this course. Please reopen it from Therapy Quest.");
        post("error", { message: "launch_failed" });
      }
    })();
  }, []);

  useEffect(() => {
    const relay = (e) => post(e.type.replace("tmc:", ""), e.detail || {});
    const events = ["tmc:progress", "tmc:item-complete", "tmc:course-complete"];
    events.forEach((n) => window.addEventListener(n, relay));
    return () => events.forEach((n) => window.removeEventListener(n, relay));
  }, []);

  useEffect(() => {
    if (state !== "ready") return;
    const report = () => post("resize", { height: document.documentElement.scrollHeight });
    report();
    const ro = new ResizeObserver(report);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [state]);

  if (state === "exchanging") return <div className="min-h-screen flex items-center justify-center bg-white" data-testid="embed-loading"><Spinner /></div>;
  if (state === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="embed-error">
      <div className="max-w-md text-center"><div className="text-base font-semibold text-slate-900">This course could not be opened</div>
        <p className="text-sm text-slate-600 mt-1.5">{error}</p></div>
    </div>
  );
  return (
    <div className="min-h-screen bg-white px-4 py-4 md:px-6" data-testid="embed-player">
      <CoursePlayer embedEnrollmentId={enrollmentId} embedded />
    </div>
  );
}
